import { useTranslation } from 'react-i18next';

import {
	Box,
	Center,
	CheckCircleSolid,
	GiftWrapOutline,
	HStack,
	IconButton,
	Image,
	Popover,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	rem,
	Switch,
	Text,
	VStack,
	XCircleSolid,
} from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { DEFAULT_GIFT_WRAP_USES } from '../../../../config/recognition';
import { useDialogControl } from '../../../../lib/dialog';
import { getCollectibleImageUrl } from '../../../../modules/rewards/reward-details-modal/components/collectible-gallery-image';
import { usePostWrapReward } from '../../../../queries/rewards';
import { getActiveCollectibleItem } from '../../../post-wrap/collectible-inventory';
import { CustomTileWrap } from '../../../post-wrap/custom-tile-wrap';
import { useActionInput } from '../../context';
import { DecorationButton } from './decoration-button';

interface GiftWrapButtonProps {
	decorationVariant?: 'default' | 'list-item';
}

export const GiftWrapButton = ({ decorationVariant = 'default' }: GiftWrapButtonProps) => {
	const { t } = useTranslation('rewards', { keyPrefix: 'reward_details.postPurchaseCollectible' });
	const user = useCurrentUser();
	const rewardDialog = useDialogControl('reward');
	const { data: collectibleReward } = usePostWrapReward();
	const activeCollectibleItem = getActiveCollectibleItem(user);
	const { wrapPost: wrapPostContext, setWrapPost } = useActionInput();
	const wrapPost = wrapPostContext ?? true;

	if (!activeCollectibleItem) {
		return (
			<DecorationButton
				label={t('addPostWrapTooltip')}
				onClick={() => collectibleReward && rewardDialog.onOpen({ rewardId: collectibleReward.id, skipPostPurchase: 'true' })}
				decorationVariant={decorationVariant}
			>
				<GiftWrapOutline />
			</DecorationButton>
		);
	}

	const isCustomPattern = activeCollectibleItem.variant_data.type === 'custom';
	const pattern = activeCollectibleItem.variant_data.type === 'avatar' ? 'avatar' : (activeCollectibleItem.variant_data.pattern ?? 'stripes');
	const color = activeCollectibleItem.variant_data.color ?? 'teal';
	const imageUrl = isCustomPattern ? undefined : getCollectibleImageUrl(pattern, color);
	const customImageUrl = activeCollectibleItem.variant_data.custom_image_url;
	const toggleLabel = wrapPost ? t('wrapPostOn') : t('wrapPostOff');

	if (decorationVariant === 'list-item') {
		return (
			<DecorationButton label={toggleLabel} onClick={() => setWrapPost?.(!wrapPost)} decorationVariant="list-item">
				<Box position="relative" display="inline-flex" color={wrapPost ? 'inherit' : 'text.secondary'}>
					<GiftWrapOutline />
					<Center position="absolute" top="-4px" right="-5px">
						{wrapPost ? <CheckCircleSolid boxSize="14px" color="primary.bg" /> : <XCircleSolid boxSize="14px" color="text.secondary" />}
					</Center>
				</Box>
			</DecorationButton>
		);
	}

	return (
		<Popover trigger="hover" placement="bottom" isLazy openDelay={300}>
			<PopoverTrigger>
				<Box>
					<IconButton
						aria-label={toggleLabel}
						borderRadius={rem(10)}
						boxSize={rem(40)}
						color="secondary.fg"
						fontSize={rem(24)}
						_hover={{ background: 'secondary.bgAlpha' }}
						icon={
							<Box position="relative" display="inline-flex">
								<GiftWrapOutline />
								<Center position="absolute" top="-4px" right="-5px">
									{wrapPost ? <CheckCircleSolid boxSize="14px" color="primary.bg" /> : <XCircleSolid boxSize="14px" color="text.secondary" />}
								</Center>
							</Box>
						}
					/>
				</Box>
			</PopoverTrigger>
			<PopoverContent w="180px">
				<PopoverBody p={3}>
					<VStack spacing={2} align="flex-start">
						<Box borderRadius="md" overflow="hidden" w="full" h="100px">
							{isCustomPattern ? (
								<CustomTileWrap src={customImageUrl as string | undefined} tileSize={40} />
							) : (
								imageUrl && <Image src={imageUrl} alt={`${pattern} ${color} gift wrap`} w="full" h="full" objectFit="cover" />
							)}
						</Box>
						<HStack spacing={2}>
							<Switch size="sm" isChecked={wrapPost} onChange={() => setWrapPost?.(!wrapPost)} />
							<Text fontSize="xs" fontWeight="medium">
								{t('wrapThisPost')}
							</Text>
						</HStack>
						<Text fontSize="xs" color="text.secondary">
							{t('remainingCount', { count: activeCollectibleItem.remaining_uses, total: DEFAULT_GIFT_WRAP_USES })}
						</Text>
					</VStack>
				</PopoverBody>
			</PopoverContent>
		</Popover>
	);
};
